/* eslint-disable no-unused-vars */
import React, { useState, useRef, useEffect } from "react"
import Header from "../../layouts/Header"
import { styled, createTheme, ThemeProvider } from "@mui/material/styles"
import {
  Box,
  Button,
  Grid,
  Typography,
  Divider,
  FormControlLabel,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
} from "@mui/material"

import BackIcon from "../../components/BackIcon"
import BuscarIcon from "../../assets/iconos/Buscar.ico"

import Pie from "./components/Pie"
import Line from "./components/Line"
import CustomDatePicker from "../../components/CustomDatePicker"
import dayjs from "dayjs"
import "dayjs/locale/es"
import CustomTextFieldReadable from "../../components/CustomTextFieldReadable"
import CustomTooltip from "../../components/ToolTip"
import fetchwrapper from "../../services/interceptors/fetchwrapper"
import CustomBackdrop from "../../components/CustomBackdrop"
import VisibilityTwoToneIcon from "@mui/icons-material/VisibilityTwoTone"
import TableLine from "./components/TableLine"

const StyledRoot = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  marginTop: theme.spacing(8),
  flexGrow: 1,
  padding: "0 16px",
  height: "100vh",
}))

const theme = createTheme({
  palette: {
    primary: {
      main: "#196C87",
    },
    secondary: {
      main: "#196C87",
    },
  },
})

const formattedCurrency = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

const convertDataToMultiSelectData = (data) => {
  const result = data.map((d, index) => ({
    id: index,
    label: d?.locdescri,
    value: d?.loccodigo,
  }))
  return result
}

const ContainerFiltros = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "repeat(12, 1fr)",
  gridTemplateRows: "auto auto auto",
  gridTemplateAreas: `
        "IAllDias IAllDias IAllDias IDia IDia IDia IMes IMes IMes IAno IAno IAno"
      `,
  gap: "8px",
  alignItems: "center",
}))

const IAllDias = styled(Box)({
  gridArea: "IAllDias",
})

const IDia = styled(Box)({
  gridArea: "IDia",
})
const IMes = styled(Box)({
  gridArea: "IMes",
})
const IAno = styled(Box)({
  gridArea: "IAno",
})

const ContainerPie = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "repeat(12, 1fr)",
  gridTemplateRows: "auto auto auto",
  gridTemplateAreas: `
        "ITable ITable ITable ITable IChart IChart IChart IChart IChart IChart IChart IChart"
      `,
  gap: "2px",
  overflowX: "auto",
  width: "100%",
  whiteSpace: "nowrap",

  [theme.breakpoints.down("md")]: {
    gridTemplateColumns: "repeat(8, 1fr)",
    gridTemplateAreas: `
      "IChart IChart IChart IChart IChart IChart IChart IChart"
      "ITable ITable ITable ITable ITable ITable ITable ITable"
    `,
  },
}))

const ITable = styled(Box)({
  gridArea: "ITable",
})
const IChart = styled(Box)({
  gridArea: "IChart",
})

const VtasDiasLocalidad = () => {
  const [filtros, setFiltros] = useState({
    fecha: null,
    day: "",
    month: "",
    year: "",
    isAllDaysSelected: false,
    fechaFiltered: null,
    dayFiltered: "",
    monthFiltered: "",
    yearFiltered: "",
    isAllDaysSelectedWhenFiltered: false,
  })

  const handleSetFiltros = (k, v) => setFiltros((prev) => ({ ...prev, [k]: v }))

  const [pieData, setPieData] = useState([])
  const [allLocalidades, setAllLocalidades] = useState([])
  const [selectedLocalidades, setSelectedLocalidades] = useState([])
  const [lineData, setLineData] = useState([])
  const [isLoadingPieData, setIsLoadingPieData] = useState(false)
  const [isLoadingLine, setIsLoadingLine] = useState(false)
  const [isFetchedPieData, setIsFetchedPieData] = useState(false)

  // Función para obtener datos del pie
  const getPieData = async () => {
    if (!filtros.month || !filtros.year) {
      alert("Digite una fecha")
      return
    }

    setIsLoadingPieData(true)
    setLineData([])
    setSelectedLocalidades([])
    setPieData([])
    setIsFetchedPieData(false)

    try {
      const options = {
        method: "POST",
        body: JSON.stringify({
          day: filtros?.day,
          month: filtros?.month,
          year: filtros?.year,
          isAllDaysSelected: filtros?.isAllDaysSelected,
        }),
        headers: {
          "Content-Type": "application/json",
          "Access-Control-Allow-Origin": "*",
        },
      }

      let response = await fetchwrapper(`/facturas_ventas/getFactVentasPie`, options)
      response = await response.json()

      // Actualizar los filtros "filtered"
      handleSetFiltros("isAllDaysSelectedWhenFiltered", filtros?.isAllDaysSelected)
      handleSetFiltros("fechaFiltered", filtros?.fecha)
      handleSetFiltros("dayFiltered", filtros?.day)
      handleSetFiltros("monthFiltered", filtros?.month)
      handleSetFiltros("yearFiltered", filtros?.year)

      // Procesar y establecer los datos del pie
      if (response?.data && response.data.length > 0) {
        const convertedDataToPieData = response.data.map((d, index) => ({
          id: index,
          value: d?.facvaltot,
          label: d?.locdescri,
        }))
        setPieData(convertedDataToPieData)
        setIsFetchedPieData(true)
      }
    } catch (error) {
      alert("Error al obtener los datos")
      console.error(error)
    } finally {
      setIsLoadingPieData(false)
    }
  }

  // Función para obtener datos de línea
  const handleSearchingLineData = async (selectedLocalidades) => {
    if (!filtros.monthFiltered || !filtros.yearFiltered || !selectedLocalidades) {
      return alert("Complete los campos")
    }

    if (!filtros.isAllDaysSelectedWhenFiltered) {
      return alert("No hay información disponible")
    }

    try {
      const options = {
        method: "POST",
        body: JSON.stringify({
          month: filtros?.monthFiltered,
          year: filtros?.yearFiltered,
          loccodigos: selectedLocalidades,
        }),
        headers: {
          "Content-Type": "application/json",
          "Access-Control-Allow-Origin": "*",
        },
      }
      setIsLoadingLine(true)
      let response = await fetchwrapper(`/facturas_ventas/getFacVentasBar`, options)
      response = await response.json()

      setLineData(response?.data || [])
      console.log(response?.data, "lineeeeeeeeee")
    } catch (error) {
      alert("No se pudo encontrar data")
    } finally {
      setIsLoadingLine(false)
    }
  }

  // Cargar localidades al montar el componente
  useEffect(() => {
    const getAllLocalidades = async () => {
      const cliciausu = localStorage.getItem("cliciausu")
      const company = JSON.parse(localStorage.getItem("jwt"))?.seleccion

      const options = {
        method: "POST",
        body: JSON.stringify({
          user: cliciausu,
          seleccion: company,
        }),
        headers: {
          "Content-Type": "application/json",
          "Access-Control-Allow-Origin": "*",
        },
      }
      try {
        let response = await fetchwrapper("/login/get_localidad", options)
        response = await response.json()
        const multiselectData = convertDataToMultiSelectData(response)
        setAllLocalidades(multiselectData)
      } catch (e) {
        alert("No se encontraron localidades")
      }
    }

    getAllLocalidades()
  }, [])

  // Efecto para limpiar datos cuando cambia "Todos los días" (sin hacer llamado a la API)
  useEffect(() => {
    // Solo limpiar datos si ya se ha hecho una búsqueda previa
    if (isFetchedPieData) {
      setPieData([])
      setLineData([])
      setIsFetchedPieData(false)
    }
  }, [filtros.isAllDaysSelected])

  // Función para el botón de búsqueda
  const handleSearching = () => {
    getPieData()
  }

  return (
    <ThemeProvider theme={theme}>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <BackIcon />
        <div style={{ display: "flex", flexDirection: "row", gap: "10px" }}>
          <div style={{ paddingTop: "10px", paddingBottom: "10px" }}>
            <CustomTooltip title={"Buscar"}>
              <Button style={{ fontSize: "0.9vw" }} onClick={handleSearching}>
                <img src={BuscarIcon} alt="Crear" style={{ width: "40px" }} />
              </Button>
            </CustomTooltip>
          </div>
        </div>
        <CustomBackdrop isLoading={isLoadingPieData || isLoadingLine} />
        <Box className={StyledRoot}>
          <CustomDatePicker
            label="Fecha"
            value={filtros.fecha}
            setValue={(v) =>
              setFiltros((prev) => {
                const diaNum = v.format("D")
                const mesNum = v.format("M")
                const anoNum = v.format("YYYY")
                return {
                  ...prev,
                  fecha: v,
                  day: diaNum,
                  month: mesNum,
                  year: anoNum,
                }
              })
            }
            slotProps={{ textField: { fullWidth: true } }}
          />
          <ContainerFiltros>
            <IAllDias>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={filtros?.isAllDaysSelected}
                    onChange={(event) => handleSetFiltros("isAllDaysSelected", event.target.checked)}
                    name="checkbox"
                    color="primary"
                  />
                }
                label="Todos los días"
              />
            </IAllDias>
            <IDia>
              <CustomTextFieldReadable label="Día" value={filtros?.isAllDaysSelected ? "Todos" : filtros?.day} />
            </IDia>
            <IMes>
              <CustomTextFieldReadable label="Mes" value={filtros?.fecha?.locale("es").format("MMMM")} />
            </IMes>
            <IAno>
              <CustomTextFieldReadable label="Año" value={filtros?.year} />
            </IAno>
          </ContainerFiltros>

          <ContainerPie>
            {isFetchedPieData && pieData.length > 0 ? (
              <ITable>
                <TableContainer component={Paper} sx={{ maxHeight: 310, overflowY: "scroll" }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        {filtros.isAllDaysSelectedWhenFiltered && (
                          <TableCell>
                            <strong>Acción</strong>
                          </TableCell>
                        )}
                        <TableCell>
                          <strong>Localidad</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Venta</strong>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {pieData.map((row, index) => (
                        <TableRow key={index}>
                          {filtros.isAllDaysSelectedWhenFiltered && (
                            <TableCell>
                              <IconButton
                                onClick={() => {
                                  if (allLocalidades.length > 0) {
                                    const l = allLocalidades.find((v) => v?.label === row.label)
                                    if (l) {
                                      setSelectedLocalidades([l])
                                      handleSearchingLineData([l])
                                    } else {
                                      alert("Error: No se pudo obtener la información de la localidad seleccionada")
                                    }
                                  } else {
                                    alert("Error: No se pudo obtener la información de la localidad seleccionada")
                                  }
                                }}
                              >
                                <VisibilityTwoToneIcon />
                              </IconButton>
                            </TableCell>
                          )}
                          <TableCell>{row.label}</TableCell>
                          <TableCell>{formattedCurrency.format(row.value)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </ITable>
            ) : null}

            <IChart>
              <Pie pieData={pieData} isFetched={isFetchedPieData} formattedCurrency={formattedCurrency} />
            </IChart>
          </ContainerPie>

          {lineData.length > 0 && filtros?.isAllDaysSelectedWhenFiltered ? (
            <>
              <Divider sx={{ backgroundColor: "#000", height: "2px", marginBlock: "50px" }} />
              <ContainerPie>
                <IChart>
                  <Line lineData={lineData} month={filtros?.monthFiltered} year={filtros?.yearFiltered} />
                </IChart>
                <ITable>
                  <TableLine
                    selectedDate={filtros?.fechaFiltered}
                    infoLine={lineData[0]?.data}
                    formattedCurrency={formattedCurrency}
                  />
                </ITable>
              </ContainerPie>
            </>
          ) : null}
        </Box>
      </div>
    </ThemeProvider>
  )
}

export default VtasDiasLocalidad
